import React from 'react';
import { Link } from 'react-router-dom';
import {
  FiArrowLeft,
  FiExternalLink,
  FiVideo,
  FiMessageCircle,
  FiTrendingUp,
  FiCreditCard,
  FiShield,
  FiCpu,
  FiArrowRight,
} from 'react-icons/fi';
import SEO from '../../components/SEO';
import './CaseStudy.css';

const ACCENT = '#14B8A6';
const LIVE_URL = 'https://sexyselfies.com/';
const IMG_MAC =
  'https://sexyselfies-content.s3.amazonaws.com/SSscreenhp-front.png';
const IMG_PHONE =
  'https://sexyselfies-content.s3.amazonaws.com/Screenshot%202026-07-13%20at%2011.05.00%E2%80%AFAM-portrait.png';

const SexySelfiesCaseStudy = () => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: 'SexySelfies — Case Study',
    headline: 'Creator Monetization Platform with Live Streaming + In-App Ad Auction',
    description:
      "How SilverFox Media designed and shipped SexySelfies: a mobile-first creator monetization platform with a custom WebRTC-to-RTMP live streaming stack, real-time Socket.io chat, an algorithm-driven discovery feed, and a self-serve in-feed ad auction — 100+ REST endpoints, 42 MongoDB models, 400+ project files.",
    image: IMG_MAC,
    url: 'https://silverfoxmedia.co/portfolio/sexyselfies',
    author: {
      '@type': 'Organization',
      name: 'SilverFox Media',
      url: 'https://silverfoxmedia.co',
    },
    about: [
      { '@type': 'Thing', name: 'Creator Economy Platform' },
      { '@type': 'Thing', name: 'WebRTC Live Streaming' },
      { '@type': 'Thing', name: 'MediaSoup SFU' },
      { '@type': 'Thing', name: 'AWS IVS' },
      { '@type': 'Thing', name: 'Stripe Payments' },
    ],
  };

  return (
    <>
      <SEO
        title="SexySelfies Case Study: Creator Platform with Live Streaming + Ad Auction"
        description="How SilverFox Media architected SexySelfies: a mobile-first creator monetization platform with WebRTC-to-RTMP live streaming (MediaSoup + FFmpeg + AWS IVS), real-time Socket.io chat, algorithm-driven discovery, and an in-feed first-price ad auction. 100+ REST endpoints, 42 MongoDB models, 400+ project files."
        keywords="SexySelfies case study, creator monetization platform, WebRTC live streaming, MediaSoup SFU, FFmpeg RTMP, AWS IVS, Socket.io chat, in-feed ad auction, mobile-first PWA, MERN stack case study"
        image="https://silverfoxmedia.co/images/og/sexyselfies.jpg"
        url="/portfolio/sexyselfies"
        type="article"
        schema={structuredData}
      />

      <article
        className="case-study"
        style={{ '--case-study-accent': ACCENT }}
      >
        {/* HERO — 2-col on desktop, media hidden on mobile */}
        <section className="case-study__hero">
          <div className="container">
            <div className="case-study__hero-grid">
              <div className="case-study__hero-inner">
                <Link to="/portfolio" className="case-study__back">
                  <FiArrowLeft />
                  Back to Portfolio
                </Link>

                <span className="case-study__category">Creator Platform</span>

                <h1 className="case-study__title">
                  SexySelfies:{' '}
                  <span className="case-study__title-accent">
                    Creator Monetization + Live Streaming
                  </span>
                </h1>

                <p className="case-study__tagline">
                  A mobile-first creator monetization platform with a custom
                  WebRTC-to-RTMP live streaming stack, real-time Socket.io chat, an
                  algorithm-driven discovery feed, credit-based payments, and a
                  self-serve in-feed ad auction. 100+ REST endpoints, 42 MongoDB
                  models, 400+ project files serving 100% mobile users.
                </p>

                <div className="case-study__hero-cta">
                  <a
                    href={LIVE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="case-study__cta-primary"
                  >
                    Visit Live Site <FiExternalLink />
                  </a>
                  <Link to="/contact" className="case-study__cta-secondary">
                    Start Your Project <FiArrowRight />
                  </Link>
                </div>

                <div className="case-study__hero-meta">
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Client</span>
                    <span className="case-study__hero-meta-value">SexySelfies</span>
                  </div>
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Industry</span>
                    <span className="case-study__hero-meta-value">Creator Economy</span>
                  </div>
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Timeline</span>
                    <span className="case-study__hero-meta-value">2025 — Ongoing</span>
                  </div>
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Role</span>
                    <span className="case-study__hero-meta-value">Sole Developer</span>
                  </div>
                </div>
              </div>

              <div className="case-study__hero-media">
                <img
                  src={IMG_MAC}
                  alt="SexySelfies platform on a MacBook mockup"
                  className="case-study__hero-macbook"
                  loading="lazy"
                />
                <img
                  src={IMG_PHONE}
                  alt="SexySelfies mobile feed on a portrait mobile screen"
                  className="case-study__hero-phone"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* FEATURED IMAGE — mobile only */}
        <section className="case-study__featured">
          <div className="container">
            <div className="case-study__featured-image-wrap">
              <img
                src={IMG_MAC}
                alt="SexySelfies platform on a MacBook mockup"
                className="case-study__featured-image"
                loading="lazy"
              />
              <img
                src={IMG_PHONE}
                alt="SexySelfies mobile feed on a portrait mobile screen"
                className="case-study__featured-phone"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* OVERVIEW */}
        <section className="case-study__section">
          <div className="container">
            <p className="case-study__section-label">The Overview</p>
            <h2 className="case-study__section-title">
              A creator platform designed as a phone-native application.
            </h2>
            <div className="case-study__prose">
              <p>
                SexySelfies is a creator monetization platform built for a 100%
                mobile user base. Members discover creators through an
                algorithm-driven feed, connect with creators they like, and buy
                content or livestream access on a credit-based micro-transaction
                model. Creators upload content, go live from their phones over a
                custom WebRTC stack, message members through real-time chat, and
                take home 80% of everything they earn.
              </p>
              <p>
                Nothing about the architecture assumes desktop. Every request path
                — from swipe-to-connect discovery to iOS-Safari live streaming to
                Stripe checkout — was designed for touch, small screens, and
                inconsistent mobile connections. A parallel self-serve ad platform
                runs inside the same feed with a first-price CPM+CPC auction and
                geo-targeted delivery so the platform earns a second revenue line
                alongside creator commissions.
              </p>
            </div>
          </div>
        </section>

        {/* CHALLENGE */}
        <section className="case-study__section" style={{ background: 'var(--color-gray-100)' }}>
          <div className="container">
            <p className="case-study__section-label">The Challenge</p>
            <h2 className="case-study__section-title">
              Live streaming from iOS Safari, at scale, without a native app.
            </h2>
            <div className="case-study__prose">
              <p>
                The hardest single problem in the build: AWS IVS's Web Broadcast SDK
                doesn't work on iOS Safari, and building native mobile apps was off
                the table. Creators had to go live from their existing phone browser
                or the whole live-streaming pillar of the product wouldn't ship.
                The solution had to look and feel like Instagram Live, work on
                iOS Safari and Chrome mobile, keep latency low, and still hand off
                to AWS IVS for HLS distribution + CDN + recording.
              </p>
              <p>
                Second, member discovery had to feel like TikTok — an infinite
                scroll feed that learns what each member likes and surfaces the
                right creator in real time. The previous version used a
                preference-vector cron that batched updates hourly; the new one
                learns from every like, purchase, and connection at insert time.
              </p>
              <p>
                Third, monetization spans four surfaces (per-content unlocks,
                paid livestreams, tipping, and a full in-feed ad auction) — each
                touching Stripe, atomic Mongo credit deductions, and per-role
                permission logic. The state machine can't drop a single event.
              </p>
            </div>
          </div>
        </section>

        {/* SOLUTION / KEY FEATURES */}
        <section className="case-study__section">
          <div className="container">
            <p className="case-study__section-label">The Solution</p>
            <h2 className="case-study__section-title">
              A MERN stack + a custom WebRTC relay + Socket.io everywhere.
            </h2>
            <p className="case-study__section-lead">
              Everything runs on a MERN foundation (MongoDB, Express, React 19,
              Node) with Socket.io namespaces for every real-time surface. Live
              streaming runs on a dedicated EC2 relay we built from scratch.
              Stripe handles credit purchases and payouts; the rest is
              inspectable server code.
            </p>

            <div className="case-study__features">
              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiVideo /></span>
                <h3 className="case-study__feature-title">Custom Live Streaming Stack</h3>
                <p className="case-study__feature-body">
                  Built a WebRTC-to-RTMP relay on EC2 (Ubuntu + MediaSoup + FFmpeg +
                  Nginx + PM2). Camera capture via getUserMedia, WebRTC via
                  mediasoup-client, FFmpeg transcodes to RTMP, AWS IVS handles HLS
                  distribution, CDN, and recording. Phone camera to live viewer in
                  under 10 seconds.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiMessageCircle /></span>
                <h3 className="case-study__feature-title">Real-Time Chat + Notifications</h3>
                <p className="case-study__feature-body">
                  Socket.io namespace with JWT auth, 1 msg/sec rate limiting, creator
                  badges, tip highlighting, and admin moderation (delete/ban). Push
                  + email notifications route through a unified pipeline so every
                  event that touches the user hits every channel.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiTrendingUp /></span>
                <h3 className="case-study__feature-title">Algorithm-Driven Discovery</h3>
                <p className="case-study__feature-body">
                  Preference vector scoring from implicit signals — browse behavior,
                  likes, purchases, connections. Recency + engagement boosting,
                  trending cold-start for new members, personalized feed ranking
                  that updates on every interaction.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiCreditCard /></span>
                <h3 className="case-study__feature-title">Credits + Stripe Payments</h3>
                <p className="case-study__feature-body">
                  Credit wallet system with atomic Mongo deductions (<code>$inc</code>
                  with <code>$gte</code> guard) for content unlocks, tips, and
                  livestream access. Stripe Checkout for top-ups, weekly automated
                  creator payouts, 80/20 revenue split, webhook-driven state.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiCpu /></span>
                <h3 className="case-study__feature-title">Self-Serve Ad Platform</h3>
                <p className="case-study__feature-body">
                  Second revenue line built into the same feed — partners pre-fund
                  a Stripe credit balance, run a first-price CPM+CPC auction with
                  geo-targeted delivery, and hit density-based tier floors. IP-to-
                  zip resolution matches the winner atomically at impression + click
                  time.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiShield /></span>
                <h3 className="case-study__feature-title">Admin Moderation Suite</h3>
                <p className="case-study__feature-body">
                  Three-role architecture (Member / Creator / Admin) with
                  dedicated dashboards. Live stream kill switch with mandatory
                  termination reason, active stream monitor, content moderation
                  with a full removalState state machine (deleted_by_admin /
                  deleted_by_creator / hidden_by_creator).
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* TECH STACK */}
        <section className="case-study__section" style={{ background: 'var(--color-gray-100)' }}>
          <div className="container">
            <p className="case-study__section-label">The Stack</p>
            <h2 className="case-study__section-title">MERN + WebRTC + everything Socket.io.</h2>
            <p className="case-study__section-lead">
              Real-time everywhere the product needed it, plain HTTP everywhere
              it didn't. Every dependency in the request path is inspectable and
              swappable.
            </p>

            <div className="case-study__tech">
              <span className="case-study__tech-chip">React 19</span>
              <span className="case-study__tech-chip">Vite</span>
              <span className="case-study__tech-chip">PWA</span>
              <span className="case-study__tech-chip">Node.js</span>
              <span className="case-study__tech-chip">Express</span>
              <span className="case-study__tech-chip">MongoDB Atlas</span>
              <span className="case-study__tech-chip">Mongoose</span>
              <span className="case-study__tech-chip">Socket.io</span>
              <span className="case-study__tech-chip">WebRTC (MediaSoup)</span>
              <span className="case-study__tech-chip">FFmpeg</span>
              <span className="case-study__tech-chip">AWS IVS</span>
              <span className="case-study__tech-chip">AWS EC2 + S3</span>
              <span className="case-study__tech-chip">Nginx</span>
              <span className="case-study__tech-chip">PM2</span>
              <span className="case-study__tech-chip">Stripe</span>
              <span className="case-study__tech-chip">Anthropic Claude</span>
              <span className="case-study__tech-chip">SendGrid</span>
              <span className="case-study__tech-chip">Node-cron</span>
              <span className="case-study__tech-chip">Render</span>
            </div>
          </div>
        </section>

        {/* RESULTS */}
        <section className="case-study__section case-study__section--dark">
          <div className="container">
            <p className="case-study__section-label">The Results</p>
            <h2 className="case-study__section-title">A phone-native product at scale.</h2>
            <p className="case-study__section-lead" style={{ color: 'rgba(255,255,255,0.75)' }}>
              SexySelfies is live at sexyselfies.com — a MERN codebase that started
              with a swipe and grew into a full creator-economy stack with two
              revenue lines.
            </p>

            <div className="case-study__stats">
              <div className="case-study__stat">
                <p className="case-study__stat-value">100+</p>
                <p className="case-study__stat-label">REST API endpoints powering three role dashboards</p>
              </div>
              <div className="case-study__stat">
                <p className="case-study__stat-value">42 models</p>
                <p className="case-study__stat-label">MongoDB collections spanning content, payments, streams, ads</p>
              </div>
              <div className="case-study__stat">
                <p className="case-study__stat-value">&lt;10 sec</p>
                <p className="case-study__stat-label">Phone camera to live viewer via the custom WebRTC relay</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="case-study__section">
          <div className="container">
            <div className="case-study__cta">
              <h2 className="case-study__cta-title">Building a real-time marketplace?</h2>
              <p className="case-study__cta-text">
                Live streaming from mobile browsers, algorithm-driven feeds,
                Stripe marketplaces, self-serve ad auctions — we architect and
                ship real-time platforms like SexySelfies end-to-end.
              </p>
              <Link to="/contact" className="case-study__cta-btn">
                Start Your Project <FiArrowRight />
              </Link>
            </div>
          </div>
        </section>
      </article>
    </>
  );
};

export default SexySelfiesCaseStudy;
